import React from "react";
import { useTranslation } from "react-i18next";

import type { ExecuteAction } from "../../types";

import { Button } from "@/components/ui/v2/button";
import type {
  ButtonProps,
  ButtonVariant,
  UiComponent,
} from "@/features/integrations/types";

const BUTTON_VARIANT_MAP: Record<
  ButtonVariant,
  "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
> = {
  Default: "default",
  Destructive: "destructive",
  Outline: "outline",
  Secondary: "secondary",
  Ghost: "ghost",
  Link: "link",
};

interface ToolbarActionsProps {
  actions: UiComponent[];
  customToolbarActions?: React.ReactNode | Record<string, React.ReactNode>;
  executeAction: ExecuteAction;
  renderChild: (child: UiComponent) => React.ReactNode;
}

/**
 * ToolbarActions - renders action buttons for the toolbar.
 * Handles button components inline and delegates other components to renderChild.
 */
function ToolbarActions({
  actions,
  customToolbarActions,
  executeAction,
  renderChild,
}: ToolbarActionsProps) {
  const { t } = useTranslation();

  const customNode =
    typeof customToolbarActions === "object" &&
    customToolbarActions !== null &&
    !React.isValidElement(customToolbarActions)
      ? (customToolbarActions as Record<string, React.ReactNode>)
      : null;

  const singleCustomNode =
    typeof customToolbarActions === "object" &&
    React.isValidElement(customToolbarActions)
      ? customToolbarActions
      : null;

  const actionElements: React.ReactNode[] = [];

  for (let i = 0; i < actions.length; i++) {
    const actionComponent = actions[i];
    if (!actionComponent || typeof actionComponent !== "object") continue;

    if (actionComponent.component === "button") {
      const actionProps = actionComponent.props as unknown as ButtonProps;
      const variantKey = (actionProps.variant ?? "Default") as ButtonVariant;
      const variant = BUTTON_VARIANT_MAP[variantKey] ?? "outline";
      const actionConfig = actionProps.action;

      const handleClick = () => {
        if (actionConfig) {
          executeAction(actionConfig.actionKey, {
            successMessage: actionConfig.successMessage,
            errorMessage: actionConfig.errorMessage,
          });
        }
      };

      actionElements.push(
        <Button
          key={actionConfig?.actionKey ?? i}
          variant={variant}
          size="sm"
          type="button"
          onClick={handleClick}
        >
          {t(actionProps.label ?? "")}
        </Button>
      );
    } else {
      actionElements.push(
        <React.Fragment key={i}>{renderChild(actionComponent)}</React.Fragment>
      );
    }
  }

  if (singleCustomNode) {
    actionElements.push(
      <React.Fragment key="custom">{singleCustomNode}</React.Fragment>
    );
  }

  if (customNode) {
    Object.entries(customNode).forEach(([key, node]) => {
      actionElements.push(<React.Fragment key={key}>{node}</React.Fragment>);
    });
  }

  if (actionElements.length === 0) return null;

  return <>{actionElements}</>;
}

export { ToolbarActions };
